import cookies from "next-cookies";

import Option from "./Option";
import LocalResponse from "./Response";
import { joinParams, isFullUrl } from "../url";
import { getDirectUrl } from "@/utils/http";

const setSSRToken = (opt: Option, ctx) => {
  const pddCookie = cookies(ctx);
  const { auc_token } = pddCookie;
  opt.addHeader("Cookie", `auc_token=${auc_token}`);
};

async function handleRes(res: Response, ctx, options: Option) {
  if (!(ctx && ctx.req)) {
    if (res.status === 504 || res.status === 510) {
      return LocalResponse.error("系统繁忙中，请稍后再试");
    }

    // 需要返回promise的情况
    if (options.originResponse) {
      return res;
    }

    try {
      const resJson = await res.json();

      if (typeof resJson !== "object" || Array.isArray(resJson)) {
        return resJson;
      }

      // 接口异常
      if (res.status === 500 || res.status === 404 || res.status === 400) {
        return new LocalResponse(
          {
            ...resJson,
            error_msg: `${res.status} ${resJson.error_msg || ""}`,
          },
          res
        );
      }

      // 登录失效
      if (resJson.error_code === 40001) {
        location.href = getDirectUrl();
      }

      return new LocalResponse(resJson, res);
    } catch (err) {
      console.log("无法处理返回的数据", res);
      return LocalResponse.error("无法处理返回的数据", res);
    }
  }

  // 服务端请求
  if (options.originResponse) {
    return res;
  }

  return res.json();
}

/**
 * 请求后台数据
 * @param url 基础路径
 * @param option 配置内容
 * @param ctx 服务端请求时使用的context
 */
async function request<T = any>(url, option: Option, ctx): Promise<T> {
  let request_url = url;
  if (!isFullUrl(url)) {
    // 拼接API_HOST
    // TODO: 这里不同于oms项目, 这个项目本地访问也是和htj一样的host, 并且接口都是这个host开头
    request_url = url.startsWith("/") ? url : `/${url}`;
  }

  /**
   * 在服务端发起的请求
   */
  if (ctx && ctx.req) {
    setSSRToken(option, ctx);
  }

  let res;
  try {
    res = await fetch(request_url, option.valueOf());
    return handleRes(res, ctx, option);
  } catch (err) {
    return LocalResponse.error(err.message) as any;
  }
}

const requests = {
  /**
   * Get请求
   * @param api 请求路径
   * @param params 拼接在url上的参数
   * @param ctx 服务端进行请求的时候，把ctx传过来
   * @param options 其他的配置项
   */
  get: async <T = any>(api, params?, ctx?, options = {} as RequestInit) => {
    const url = joinParams(api, params);
    const opt = new Option("GET", options);
    return request<T>(url, opt, ctx);
  },

  /**
   * POST请求
   * @param api 请求路径
   * @param data body上的参数
   * @param params 拼接在url上的参数
   * @param ctx 服务端进行请求的时候，把ctx传过来
   * @param options 其他的配置项
   */
  post: async <T = any>(
    api,
    data?,
    params?,
    ctx?,
    options = {} as RequestInit
  ) => {
    const url = joinParams(api, params);
    const opt = new Option("POST", options);
    opt.setBody(data);
    return request<T>(url, opt, ctx);
  },

  /**
   * PUT请求
   * @param api 请求路径
   * @param data body上的参数
   * @param params 拼接在url上的参数
   * @param ctx 服务端进行请求的时候，把ctx传过来
   * @param options 其他的配置项
   */
  put: async <T = any>(
    api,
    data?,
    params?,
    ctx?,
    options = {} as RequestInit
  ) => {
    const url = joinParams(api, params);
    const opt = new Option("PUT", options);
    opt.setBody(data);
    return request<T>(url, opt, ctx);
  },

  /**
   * DELETE请求
   * @param api 请求路径
   * @param data body上的参数
   * @param params 拼接在url上的参数
   * @param ctx 服务端进行请求的时候，把ctx传过来
   * @param options 其他的配置项
   */
  delete: async <T = any>(
    api,
    data?,
    params?,
    ctx?,
    options = {} as RequestInit
  ) => {
    const url = joinParams(api, params);
    const opt = new Option("DELETE", options);
    opt.setBody(data);
    return request<T>(url, opt, ctx);
  },
};

export default requests;
